import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApolloError } from '@apollo/client/core';
import { unparse } from 'papaparse';
import { MenuItem } from 'primeng/api';
import { EMPTY, Subject, Subscription, from } from 'rxjs';
import { catchError, concatMap, debounceTime, finalize, switchMap, take, tap } from 'rxjs/operators';

import { DataOnlyDocument, RunJobCalculatorGQL, SetCrewDocFlagsGQL, UpdateJobGQL } from '../../../generated/graphql.generated';
import { ColorSchemeService } from '../../color-scheme.service';
import { PlusApollo } from '../../core/auth/graphql.module';
import { dayjs } from '../../core/dayjs/dayjs';
import { GraphQLModule } from '../../core/public-api';
import {
  assertCrewDocFlagsSaved,
  CONTRACT_FLAG,
  nowSeconds,
  UserDocFlag,
  USER_DOC_FLAG_LABELS,
} from '../../job/job-workorders/mr-calculator/crew-pay-table/crew-doc-flags';
import { BrandingService } from '../../services/branding.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import {
  JOB_EXECUTION_LABELS,
  JOB_EXECUTION_PARTNER_FP,
  JOB_EXECUTION_THIRD_PARTY,
  jobExecutionZeroesFpPayout,
  normalizeJobExecutionType,
} from '../../shared/job-execution-type';
import { parseEmbeddedUrl } from '../../utilities/generic.util';

import {
  DashboardStore,
  mergeInDefaultFilters,
  OTR_BOOKED_JOB_STAGES,
  OTR_INTERSTATE_ZONE_ID,
  OTR_LABOR_DATA_COLUMNS,
} from '../dashboardv2.store';

import { FranchiseChip, franchiseChips } from './franchise-chip-colors';
import { buildLaborExportRows, laborExportFileName } from './otr-labor-export';
import { compareCrewWithinJob } from './otr-labor-row-order';

const CALC_INPUT_PREFIX = 'calculatorInput__crewPay__';

// Jobs saved per request. Each job is a heavy sequential server-side recompute, so Save
// is chunked to keep any one request well under the gateway timeout; the backend rejects
// batches larger than this (MAX_JOBS_PER_RUN_CALCULATOR — keep the two in sync). For <=
// this many jobs it's still one updateJob + one runJobCalculator.
const SAVE_BATCH_SIZE = 10;

// Per-crew-member crew pay pulled out of a runJobCalculator `state` blob (in dollars).
interface CrewMemberPay {
  crewMemberId: string;
  crewLead?: number; flatRate?: number; packing?: number; unpacking?: number;
  load?: number; unload?: number; bonus?: number;
  fees?: number; tip?: number; total?: number; paid?: number; unpaid?: number;
}

// Flattened crewPay output suffix → CrewMemberPay field. The total is the suffix-less key
// `crewPay__<crewMemberId>` (mapped from ''); stored values are cents.
const CREW_PAY_OUTPUT_FIELDS: Record<string, keyof Omit<CrewMemberPay, 'crewMemberId'>> = {
  '': 'total', crewLead: 'crewLead', flatRate: 'flatRate', packing: 'packing',
  unpacking: 'unpacking', load: 'load', unload: 'unload', bonus: 'bonus',
  fees: 'fees', tip: 'tip', paid: 'paid', unpaid: 'unpaid',
};

// The jobCrewMember data columns (one row per job × crew member) live in the store, shared
// with the 'otrLaborTracking' drill-down link so the two cannot drift. `job` / `customer` /
// `crewMember` come back as `text <url>` links (linkFormat: 'web').

// Per-man rates (dollars) and crew sizes the calculator's crew-pay table uses for fee
// math, the enabled-checkbox amounts, and the over/under-count + disabled-cell behavior.
// Read via `calculator_<key>` columns appended to the SAME jobCrewMember row query
// (the engine allows calculator_ for jobCrewMember; job is in scope, like crewPay__).
const FP_LABOR_KEYS = {
  crewLead: 'fpLabor_crewLead',
  flatRate: 'fpLabor_flatRatePerMan',
  packing: 'fpLabor_packingLaborPerMan',
  unpacking: 'fpLabor_unpackingLaborPerMan',
  load: 'fpLabor_loadPerMan',
  unload: 'fpLabor_unloadPerMan',
  bonus: 'fpLabor_bonusPerMan',
  // Requirement: `#7194` - what the member ticked as Crew Lead earns for Load / Unload. Only output
  // by calculator versions from 2611.03.0; older jobs fall back to the regular figure (see
  // `CREW_LEAD_RATES_MIN_VERSION`).
  crewLeadLoad: 'fpLabor_crewLeadLoad',
  crewLeadUnload: 'fpLabor_crewLeadUnload',
} as const;

// Requirement: `#7194` - the first calculator version that pays the Crew Lead rate on Load / Unload.
//
// The two Crew Lead figures are only trusted when the job was last saved by this version or later.
// Their mere presence is not enough: saved calculator output is MERGED into the job, never replaced,
// so a job saved under 2611.03.0 and then moved back to an older script keeps the Crew Lead figures
// from the newer run, and the older script - which pays the lead the member amount - never
// overwrites them. Every OTR script since 2608.26.0 saves its own `calculatorVersion`, so moving
// back overwrites it with the older number and the check fails as it should.
const CREW_LEAD_RATES_MIN_VERSION = [ 2611, 3, 0 ];

const CREW_SIZE_KEYS = {
  packing: 'packingCrewSize',
  unpacking: 'unpackingCrewSize',
  load: 'loadCrewSize',
  unload: 'unloadCrewSize',
  driving: 'drivingCrewSize',
} as const;

// Calculator columns appended to the jobCrewMember row query (no separate fetch).
const OTR_LABOR_CALC_COLUMNS = [
  ...Object.values(FP_LABOR_KEYS).map((k) => `calculator_${k}`),
  ...Object.values(CREW_SIZE_KEYS).map((k) => `calculator_${k}`),
  // Requirement: `#7194` - gates the Crew Lead Load / Unload figures (`CREW_LEAD_RATES_MIN_VERSION`).
  'calculator_calculatorVersion',
  // Requirement: `#6620` - the RAW execution type, which this component branches on. The engine's
  // mapped `jobExecutionType` column ("Execution Type") is what the drill-down report shows; it is
  // deliberately not used here, because comparing against a display label defined in another
  // package breaks silently the moment someone rewords it. Only requested by the dashboard, so the
  // drill-down never receives both and the two headers cannot be confused.
  'calculator_jobExecutionType',
];

// Requirement: `#6892` - the manual "Total fees for services" override (dollars, blank when unset),
// appended to the SAME jobCrewMember row query. Dashboard-only, unlike `OTR_LABOR_DATA_COLUMNS`:
// that list also builds the drill-down report, which shows the calculator's own override-aware
// `Crew Pay: Fees for Services` and so needs nothing added. Only this component has to know whether
// an override EXISTS -- to hold the amount steady while its phase checkboxes are toggled, and to
// show what it was overridden away from.
const OTR_LABOR_INPUT_COLUMNS = [ 'crewLaborFeesOverride' ];

// Header for `crewLaborFeesOverride`, as DATA_COLUMNS spells it in the reports engine.
const FEES_OVERRIDE_HEADER = 'Fees Override';

// Header for `crewPayFees` -- the calculator's own resolved "Total fees for services" (override
// applied, forwarding applied), which the dashboard reads rather than re-deriving.
const CREW_PAY_FEES_HEADER = 'Crew Pay: Fees for Services';

// Mirrors the engine's header for a `calculator_<key>` column:
// strToTitleCase(key.replace(/([A-Z])/g, ' $1')) (previewReport.ts). Used to look up
// the calc columns by their emitted header without hardcoding the brittle label strings.
// Requirement: `#7194` - compares a saved calculator version (`YYMM.DD.N`, e.g. '2611.03.0') with
// `min`, part by part. Anything that does not parse - a job never calculated, or the engine's
// literal 'None' for an empty cell - counts as older.
function isCalculatorVersionAtLeast(version: string, min: number[]): boolean {
  const parts = version.trim().split('.').map(Number);
  if (parts.length !== min.length || parts.some((part) => !Number.isFinite(part))) {
    return false;
  }
  for (let i = 0; i < min.length; i++) {
    if (parts[i] !== min[i]) {
      return parts[i] > min[i];
    }
  }
  return true;
}

function calcColHeader(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// Requirement: `#6620` - how the job is executed. Derived rather than hardcoded, like the other
// calc columns: this one has no DATA_COLUMNS entry (it rides the generic `calculator_` pattern),
// so its header is whatever the engine's title-casing produces — "Job Execution Type".
const JOB_EXECUTION_TYPE_HEADER = calcColHeader('jobExecutionType');

// Requirement: `#6538` - report columns the pay math cannot be computed without. Their headers
// come from DATA_COLUMNS (crewLaborAssignedPhases / crewLaborPaidToFp) in the reports engine.
// Requirement: `#6620` - the execution type joins them: without it a Third-Party job's crew-count
// expectations are applied anyway, firing an under-count warning on every phase.
// Requirement: `#6892` - two more join them. Without the override every overridden row silently
// falls back to the sum of the phase columns -- the exact bug this work fixes, returning with no
// error anywhere -- and without the saved fee itself, now READ rather than re-derived, `money()`
// returns 0 and every row reports its crew member as owed nothing. A loud failure beats a
// plausible wrong figure on a sheet that authorises payments.
const REQUIRED_PAY_COLUMNS = [
  'Assigned Phases', 'Pay Labor to FP', JOB_EXECUTION_TYPE_HEADER,
  FEES_OVERRIDE_HEADER, CREW_PAY_FEES_HEADER,
];

interface JobCalcData {
  rates: {
    crewLead: number; flatRate: number; packing: number; unpacking: number; load: number; unload: number; bonus: number;
    crewLeadLoad: number; crewLeadUnload: number;
  };
  crewSize: { packing: number; unpacking: number; load: number; unload: number; driving: number };
}

interface OtrLaborRow {
  jobId: string;
  jobCode: string;
  jobUrl: string;
  // Requirement: `#6751` - comma-separated names of the franchises the job's events were shared
  // into, straight from the report's `Shared With Franchises` column. Empty for a job that was
  // never shared, which renders (and exports) as a blank cell rather than a dash: "not shared" is
  // a fact about the job, not a column that fails to apply to it.
  sharedFranchises: string;
  // Requirement: `#6990` - `sharedFranchises` split into coloured chips for display. Built once per
  // load rather than in the template, so the colour lookup is not re-run on every change detection.
  sharedFranchiseChips: FranchiseChip[];
  customerName: string;
  lastEventDate: string;
  creationDate: string;
  crewMemberId: string;
  crewMemberName: string;
  crewLeadRate: number;
  flatRateRate: number;
  packingRate: number;
  unpackingRate: number;
  loadRate: number;
  unloadRate: number;
  // Requirement: `#7194` - Load / Unload for this member while they are ticked as Crew Lead. Kept
  // alongside the regular figures so toggling Crew Lead re-prices the row without a refetch.
  crewLeadLoadRate: number;
  crewLeadUnloadRate: number;
  bonusRate: number;
  // The fee actually owed. Read from the calculator's saved output on load; re-resolved locally by
  // `computeFees` only while the user is editing the row.
  fees: number;
  // Requirement: `#6892` - the manual "Total fees for services" override in dollars, or null when
  // this member's fee is the sum of their enabled phase columns. Needed as its own field because
  // the saved fee cannot answer "was this overridden?": an override of 0 and an unassigned member
  // are the same number there, and a toggled checkbox must not move an overridden fee.
  feesOverride: number | null;
  // Requirement: `#6892` - what the enabled phase columns come to on their own, kept even when an
  // override supersedes it so the cell can show BOTH figures. Without it an overridden row silently
  // stops responding to its own checkboxes and nothing on screen says why.
  feesCalculated: number;
  tip: number;
  paid: number;
  crewLeadEnabled: boolean;
  flatRateEnabled: boolean;
  packingEnabled: boolean;
  unpackingEnabled: boolean;
  loadEnabled: boolean;
  unloadEnabled: boolean;
  bonusEnabled: boolean;
  // Per-cell disabled state — mirrors the calculator's "—" cells. Set by applyComputedState.
  // Crew Lead is never disabled (its calculator gate uses a stale-prone persisted rate);
  // Reg. Crew is disabled on single-crew jobs; the rest follow their crew size.
  flatRateDisabled: boolean;
  packingDisabled: boolean;
  unpackingDisabled: boolean;
  loadDisabled: boolean;
  unloadDisabled: boolean;
  bonusDisabled: boolean;
  warnings: string[];
  total: number;
  unpaid: number;
  // Requirement: `#6538` - manual override: the Franchise Partner pays this member locally, so the
  // calculator zeroes their crew pay and forwards it to the FP. Read from its own report column
  // rather than inferred from $0 amounts, which are indistinguishable from "not assigned".
  // While set, the phase checkboxes are locked: their `__enabled` inputs are preserved in the
  // calculator, but this dashboard cannot recover them from the (zeroed) amounts.
  payToFp: boolean;
  // Requirement: `#6620` - how the job was executed, and the Third-Party shorthand derived from it.
  // Carried as its own field rather than being folded into `payLocked()` so that how these rows
  // RENDER stays a one-line decision: a Third-Party job funds no FP labor, but the crew amounts are
  // deliberately preserved (the calculator keeps them and only locks editing), so they are shown.
  jobExecutionType: string;
  jobExecutionLabel: string;
  isThirdParty: boolean;
  // Requirement: `#6620` - this member is not paid from this job: their labor is either forwarded
  // to the Franchise Partner (`#6538`) or covered by the outside agency running a Third-Party job.
  // Both zero the same figures and lock the same controls, so the pay math takes one flag.
  laborPaidElsewhere: boolean;
  // Document-compliance flags. contractForThisJob is per (job × crew member); achInfo/w9 are
  // per crew member (global). Rendered as the three rightmost checkbox columns.
  contractForThisJob: boolean;
  achInfo: boolean;
  w9: boolean;
  // A job with no crew assigned still returns one row (crew matched via OPTIONAL
  // MATCH → null crew). Such rows are read-only placeholders: no editable cells,
  // never in pendingByJob, and excluded from the per-job crew calculations.
  noCrew: boolean;
  // Requirement: `#6751` - zebra striping by JOB rather than by row: every row of the same job
  // shares this flag, and it flips on each new job within a payment period. A crew member's row is
  // only meaningful next to the rest of their job's crew, and with 20-odd columns the eye loses the
  // job boundary long before it reaches Unpaid. Assigned in `groupRows`, which is also what
  // guarantees a job's rows are contiguous in the first place.
  jobStripe: boolean;
}

export interface PaymentPeriodGroup {
  periodStart: string;
  label: string;
  payoutDate?: string;
  payoutIso?: string;
  jobCount: number;
  rows: OtrLaborRow[];
}

@Component({
  selector: 'app-dashboard-otr-labor-tracking',
  standalone: false,
  templateUrl: './dashboard-otr-labor-tracking.component.html',
  styleUrls: [
    '../dashboardv2.component.scss',
    './dashboard-otr-labor-tracking.component.scss',
  ],
})
export class DashboardOtrLaborTrackingComponent implements OnInit, OnDestroy {

  periodGroups: PaymentPeriodGroup[] = [];
  expandedPeriods = new Set<string>();
  loading = true;
  lastReload = 0;
  error: string | null = null;
  // Requirement: `#6538` - set when a response arrives without the columns the pay figures are
  // derived from, so the rows are dropped rather than rendered wrong. Distinct from `error` (which
  // also covers request failures) because it must suppress the "no jobs in this period" empty
  // state — that message would be an outright false statement here — and block Save.
  stale = false;

  private reload$ = new Subject<void>();
  private subs: Subscription[] = [];
  // Per-job calculator data (fpLabor rates + crew sizes), keyed by jobId.
  private jobCalc = new Map<string, JobCalcData>();
  // Dirty accumulator: staged edits merged per job (jobId → calculatorInput__* metadata),
  // flushed only on an explicit Save (one updateJob + one calculator recompute per job).
  private pendingByJob = new Map<string, Record<string, string>>();
  saving = false;
  // In-flight per-user doc-flag writes (ACH/W-9), keyed `${crewMemberId}::${flag}`, so the
  // affected rows can show an inline saving spinner while setCrewDocFlags is running.
  private savingDocFlags = new Set<string>();
  // Snapshot of the last authoritative row values (keyed by job × crew member) so Discard
  // can revert staged edits locally without a refetch. Refreshed on load and, per
  // successfully-saved job, after Save.
  private rowSnapshots = new Map<string, OtrLaborRow>();

  // Requirement: #6598 - the dashboard period's display label, used to name export files.
  private displayPeriod = '';

  // Requirement: #6598 - drill-down into the report viewer, where the same crew-pay rows can
  // be saved as a report, scheduled, or exported semi-live to Google Sheets. Link-only: the
  // dashboard table is fetched by buildQueryVars below (it needs extra calculator_* columns).
  laborTrackingReportParams$ = this.dashboardStore.getReportQueryParams('otrLaborTracking');

  // Split-button dropdown. The array identity is kept stable (p-splitButton re-reads
  // [model] on every change-detection pass); the report link's queryParams are mutated in
  // place by the subscription in ngOnInit once the zone and period resolve.
  exportActions: MenuItem[] = [
    {
      label: 'Download as CSV',
      icon: 'pi pi-download',
      command: () => this.downloadCsv(),
    },
    {
      label: 'See crew pay rows',
      icon: 'pi pi-eye',
      routerLink: [ '/reports', 'dynamic', 'new' ],
      target: '_blank',
    },
  ];

  triggerReload = (): void => { this.reload$.next(); };

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly apollo: PlusApollo,
    private readonly updateJobGQL: UpdateJobGQL,
    private readonly runJobCalculatorGQL: RunJobCalculatorGQL,
    private readonly setCrewDocFlagsGQL: SetCrewDocFlagsGQL,
    private readonly notify: FreyaNotificationsService,
    private readonly graphqlModule: GraphQLModule,
    private readonly clipboard: Clipboard,
    private readonly brandingService: BrandingService,
    private readonly colorScheme: ColorSchemeService,
  ) {}

  // Requirement: `#6990` - the franchise chips and the row tints use fixed colours, so they need a
  // light and a dark variant; the theme switch swaps stylesheets and leaves no class to key the dark
  // set off.
  get isDark(): boolean { return this.colorScheme.darkMode; }

  ngOnInit(): void {
    this.subs.push(
      this.dashboardStore.unixPeriod$.subscribe(() => {
        this.reload$.next();
      }),
    );

    // Requirement: #6598 - export filename carries the period the data is for.
    this.subs.push(
      this.dashboardStore.displayPeriod$.subscribe((displayPeriod) => {
        this.displayPeriod = displayPeriod;
      }),
    );

    // Mutate in place rather than rebuilding exportActions, to keep the [model] array
    // identity stable for p-splitButton.
    this.subs.push(
      this.laborTrackingReportParams$.subscribe((queryParams) => {
        const reportAction = this.exportActions
          .find((action) => action.label === 'See crew pay rows');
        if (reportAction) {
          reportAction.queryParams = queryParams;
        }
      }),
    );

    this.subs.push(
      this.reload$.pipe(
        debounceTime(100),
        switchMap(() => {
          const zone = this.graphqlModule.zone.value;
          if (!zone) {
            this.loading = false;
            return EMPTY;
          }

          this.loading = true;
          this.error = null;
          this.stale = false;

          return this.dashboardStore.unixPeriod$.pipe(
            take(1),
            // One query, windowed + bucketed by the last scheduled event date — includes
            // every job whose last event ends in-window, regardless of completion status.
            switchMap(({ start, end }) => this.apollo.query({
              query: DataOnlyDocument,
              fetchPolicy: 'network-only',
              variables: this.buildQueryVars('lastScheduledWorkEventEndDate', start, end, zone),
            })),
            tap((result: any) => {
              const headers = result?.data?.previewReport?.data?.headers ?? [];
              const rows = result?.data?.previewReport?.data?.rows ?? [];

              // Requirement: `#6538` - every pay figure on this dashboard is derived in the browser
              // from these two columns; neither has a fallback. Without "Assigned Phases" every row
              // reads as unassigned and owed nothing; without "Pay Labor to FP" it is worse — labor
              // already forwarded to the franchise partner shows as owed to the crew member, with
              // Pay and Pay all live, inviting a second payment. Drop the response rather than
              // render a pay sheet someone could act on. Nothing has been written at this point, so
              // the refresh button is a complete recovery.
              const missingColumns = rows.length
                ? REQUIRED_PAY_COLUMNS.filter((column) => !headers.includes(column))
                : [];
              if (missingColumns.length) {
                this.stale = true;
                this.error = `Crew pay columns missing from the report response (${missingColumns.join(', ')}).`
                  + ' Figures cannot be shown — use refresh to try again.';
                this.notify.error('Error', this.error);
                this.periodGroups = [];
                this.loading = false;
                this.lastReload = Math.floor(Date.now() / 1000);
                return;
              }

              // Calc rates/crew sizes ride along in the row results — build the per-job
              // map from the same rows (no separate query).
              this.jobCalc = this.buildJobCalcMap(headers, rows);

              const groups = this.groupRows(
                this.dropRedundantPlaceholders(this.parseRows(headers, rows)),
              );
              this.applyComputedState(groups);
              this.periodGroups = groups;

              // These rows have just replaced the ones any staged edits were made against, so those
              // edits are no longer visible anywhere — yet they would still be written by Save, and
              // after a period change they may belong to jobs that are no longer even in view. Drop
              // them, and say so: silently committing edits nobody can check is the worse outcome on
              // a sheet that authorises payments. Deliberately only on this path — the stale and
              // request-failure paths leave the rows alone, so their edits stay valid and Save is
              // already blocked. Not `discardChanges()`, which would also restore the old rows over
              // the fresh ones.
              // Skipped while a save is in flight: that flow owns the map (it clears each job as it
              // succeeds and deliberately leaves failed ones staged for retry), and the warning
              // would claim edits were discarded at the moment they are being written.
              const discardedJobs = this.saving ? 0 : this.pendingByJob.size;
              if (discardedJobs) {
                this.pendingByJob.clear();
                this.notify.warning(
                  'Unsaved changes discarded',
                  `${discardedJobs} job${discardedJobs === 1 ? '' : 's'} had unsaved crew pay edits.`
                    + ' They were discarded because the data was reloaded.',
                );
              }

              // Reset the discard snapshot to this freshly-loaded authoritative state.
              this.rowSnapshots.clear();
              this.snapshotRows();
              this.loading = false;
              this.lastReload = Math.floor(Date.now() / 1000);
            }),
            catchError(() => {
              this.error = 'Failed to load OTR labor data.';
              this.loading = false;
              this.lastReload = Math.floor(Date.now() / 1000);
              return EMPTY;
            }),
          );
        }),
      ).subscribe(),
    );

    this.reload$.next();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.reload$.complete();
  }

  private col(headers: string[], row: string[], name: string): string {
    const idx = headers.indexOf(name);
    return idx !== -1 ? (row[idx] ?? '') : '';
  }

  private buildQueryVars(
    // Requirement: `#6855` - a LITERAL type, not a widened string, deliberately: the drill-down in
    // dashboardv2.store.ts ('otrLaborTracking') must use the same periodType, and pinning it here
    // means changing one without the other fails `tsc` instead of silently showing the operator a
    // different job set than the link they clicked.
    periodType: 'lastScheduledWorkEventEndDate',
    start: number,
    end: number,
    zone: string,
  ) {
    return {
      start,
      end,
      periodType,
      groupBy: 'jobCrewMember',
      // Row data + the per-job calculator rates/crew sizes in ONE query.
      dataColumns: [ ...OTR_LABOR_DATA_COLUMNS, ...OTR_LABOR_CALC_COLUMNS, ...OTR_LABOR_INPUT_COLUMNS ],
      // Restrict to interstate-owned OTR jobs (mirrors the 6280 interstate query).
      filter: mergeInDefaultFilters('All', {
        job: {
          franchiseIds: [ OTR_INTERSTATE_ZONE_ID ],
          // Requirement: `#6751` - committed work only, the same stage filter OTR - System Revenue
          // uses. Must be kept identical to the `otrLaborTracking` report case in the store, which
          // is what the "See crew pay rows" link opens: a drill-down showing a different set of
          // jobs from the table it drills into is worse than no drill-down.
          getJobsOfStage: OTR_BOOKED_JOB_STAGES,
        },
      }),
      // `text <url>` link form so jobId / crewMemberId are recoverable from the URL.
      linkFormat: 'web',
      sort: null,
      performance: 'skip-count',
      skip: 0,
      limit: -1,
      zone,
    } as any;
  }

  private buildJobCalcMap(headers: string[], rows: string[][]): Map<string, JobCalcData> {
    const map = new Map<string, JobCalcData>();
    const num = (row: string[], key: string) => {
      const idx = headers.indexOf(calcColHeader(key));
      return idx !== -1 ? (parseFloat((row[idx] ?? '').replace(/[^0-9.-]/g, '')) || 0) : 0;
    };

    for (const row of rows) {
      const job = this.parseEntity(this.col(headers, row, 'Job'));
      if (!job.id) { continue; }
      const hasCrewLeadRates = isCalculatorVersionAtLeast(
        this.col(headers, row, calcColHeader('calculatorVersion')),
        CREW_LEAD_RATES_MIN_VERSION,
      );
      map.set(job.id, {
        rates: {
          crewLead: num(row, FP_LABOR_KEYS.crewLead),
          flatRate: num(row, FP_LABOR_KEYS.flatRate),
          packing: num(row, FP_LABOR_KEYS.packing),
          unpacking: num(row, FP_LABOR_KEYS.unpacking),
          load: num(row, FP_LABOR_KEYS.load),
          unload: num(row, FP_LABOR_KEYS.unload),
          bonus: num(row, FP_LABOR_KEYS.bonus),
          crewLeadLoad: num(row, hasCrewLeadRates ? FP_LABOR_KEYS.crewLeadLoad : FP_LABOR_KEYS.load),
          crewLeadUnload: num(row, hasCrewLeadRates ? FP_LABOR_KEYS.crewLeadUnload : FP_LABOR_KEYS.unload),
        },
        crewSize: {
          packing: num(row, CREW_SIZE_KEYS.packing),
          unpacking: num(row, CREW_SIZE_KEYS.unpacking),
          load: num(row, CREW_SIZE_KEYS.load),
          unload: num(row, CREW_SIZE_KEYS.unload),
          driving: num(row, CREW_SIZE_KEYS.driving),
        },
      });
    }
    return map;
  }

  // Parse a `text <url>` entity cell (linkFormat: 'web') into its label and the
  // trailing path id (job → /jobs/{id}, crew member / customer → /user/{id}).
  private parseEntity(cell: string): { label: string; id: string } {
    const parsed = parseEmbeddedUrl(cell);
    if (!parsed) {
      return { label: cell && cell !== 'None' ? cell : '', id: '' };
    }
    const id = parsed.url.split('/').filter(Boolean).pop() ?? '';
    return { label: parsed.before, id };
  }

  private parseRows(headers: string[], rows: string[][]): OtrLaborRow[] {
    const result: OtrLaborRow[] = [];

    for (const row of rows) {
      const get = (name: string) => this.col(headers, row, name);
      // The crewPay columns are in CURRENCY_COLS, so the dynamic engine already
      // currency-formats them AND divides the stored cents by the currency multiple
      // (formatCurrency, multiple=100). Values arrive as "$120.00" — strip the
      // formatting; no further cents conversion (unlike the old system report).
      const money = (name: string) => parseFloat(get(name).replace(/[^0-9.-]/g, '')) || 0;

      const job = this.parseEntity(get('Job'));
      const jobId = job.id;
      if (!jobId) { continue; }
      const jobUrl = `/jobs/v4/${jobId}/overview`;

      // Requirement: `#6751` - already a comma-separated display string from the engine, so it is
      // taken verbatim: nothing here splits or re-joins it, which is how the dashboard and the
      // drill-down report stay guaranteed to show the same text for the same job.
      const sharedFranchises = get('Shared With Franchises');
      const sharedFranchiseChips = franchiseChips(sharedFranchises);

      const customerName = this.parseEntity(get('Customer')).label;

      // Window + bucket by the last scheduled event date. The column returns a real
      // 'yyyy-MM-dd HH:mm' for every windowed job (fallback 'No Events Scheduled' won't
      // occur, since the window matches only jobs with an event in-range); guard on date
      // validity rather than a sentinel string.
      const lastEventParsed = dayjs(get('Last Scheduled Event End Date & Time'));
      const lastEventDate = lastEventParsed.isValid() ? lastEventParsed.format('YYYY-MM-DD') : '';
      const creationDate = get('Job Created At');

      const crew = this.parseEntity(get('Crew Member'));
      const crewMemberId = crew.id;
      // jobCrewMember matches crew via OPTIONAL MATCH, so a job with no crew assigned
      // still returns one row (null crew). Surface it as a read-only placeholder rather
      // than dropping the job entirely.
      if (!crewMemberId) {
        result.push(this.buildPlaceholderRow({
          jobId,
          jobCode: job.label,
          jobUrl,
          sharedFranchises,
          sharedFranchiseChips,
          customerName,
          lastEventDate,
          creationDate,
        }));
        continue;
      }

      // Requirement: `#6538` - phase assignments come from the engine's crewLaborAssignedPhases
      // column, which reads the manual `__enabled` inputs. This replaced inferring them from the
      // persisted amount (`> 0`), which could not survive the "Pay Labor to FP" override (it
      // zeroes every amount) and also mis-read any phase enabled at a $0 per-man rate. The RATE
      // still comes from the fpLabor per-man values (calc map), never from the crew-pay outputs.
      const assignedPhases = new Set(
        get('Assigned Phases').split(',').map((phase) => phase.trim()).filter(Boolean),
      );
      const crewLeadEnabled = assignedPhases.has('crewLead');
      const flatRateEnabled = assignedPhases.has('flatRate');
      const packingEnabled = assignedPhases.has('packing');
      const unpackingEnabled = assignedPhases.has('unpacking');
      const loadEnabled = assignedPhases.has('load');
      const unloadEnabled = assignedPhases.has('unload');
      const bonusEnabled = assignedPhases.has('bonus');

      const calc = this.jobCalc.get(jobId);
      const crewLeadRate = calc?.rates.crewLead ?? 0;
      const flatRateRate = calc?.rates.flatRate ?? 0;
      const packingRate = calc?.rates.packing ?? 0;
      const unpackingRate = calc?.rates.unpacking ?? 0;
      const loadRate = calc?.rates.load ?? 0;
      const unloadRate = calc?.rates.unload ?? 0;
      const crewLeadLoadRate = calc?.rates.crewLeadLoad ?? 0;
      const crewLeadUnloadRate = calc?.rates.crewLeadUnload ?? 0;
      const bonusRate = calc?.rates.bonus ?? 0;

      const tip = money('Crew Pay: Tip');
      const paid = money('Crew Pay: Paid');

      // Document-compliance flags arrive as 'Yes'/'No' (engine crewDoc columns).
      const contractForThisJob = get('Contract For This Job') === 'Yes';
      const achInfo = get('ACH Info') === 'Yes';
      const w9 = get('W-9') === 'Yes';

      // Requirement: `#6620` - the raw calculator value ('partnerFp' | 'otrOperated' | 'thirdParty').
      //
      // Anything unrecognised means "no execution type recorded" and is normalised to Partner FP.
      // That covers two real cases, and a `|| fallback` covers neither: a job last calculated before
      // #6620 has no value at all, and the engine serialises a null cell as the literal string
      // 'None' (previewReport.formatters) — which is truthy, so it would otherwise be rendered as an
      // execution type called "None". Matches the ELSE branch of the engine's own mapped column, so
      // the raw and mapped columns can never disagree about the same job.
      const jobExecutionType = normalizeJobExecutionType(get(JOB_EXECUTION_TYPE_HEADER));
      const isThirdParty = jobExecutionType === JOB_EXECUTION_THIRD_PARTY;

      // Requirement: `#6538` - a Yes/No engine column (crewLaborPaidToFp), read from the manual
      // input flag.
      //
      // Requirement: `#6620` - neutralised when the job type leaves no Franchise Partner to forward
      // to, exactly as the calculator does. The column reports the RAW stored flag, which the
      // calculator ignores on those job types — so taking it at face value would show a member as
      // forwarded (zeroed, locked, no Pay button) while the calculator pays them in full.
      const payToFp = jobExecutionType === JOB_EXECUTION_PARTNER_FP && get('Pay Labor to FP') === 'Yes';

      // Requirement: `#6620` - a Third-Party job funds no FP labor, so nobody is paid from it. Both
      // reasons zero the same figures, so the pay math takes one flag rather than two.
      const laborPaidElsewhere = payToFp || isThirdParty;

      // Requirement: `#6892` - the manual "Total fees for services" override, straight from the
      // calculator INPUT. Deliberately not parsed with `money()` above: its `|| 0` coerces an
      // absent value to 0, and 0 is a legitimate override (a member assigned to phases who is
      // nonetheless owed nothing for them). The engine renders an unset cell as the literal
      // 'None', which strips to NaN -- hence the explicit null rather than a falsy test.
      //
      // Dollars already: this is the raw input, not one of the cents-valued `crewPay*` outputs, so
      // it takes the same formatting strip as `money()` but no currency division.
      const feesOverrideNum = parseFloat(get(FEES_OVERRIDE_HEADER).replace(/[^0-9.-]/g, ''));
      const feesOverride = Number.isNaN(feesOverrideNum) ? null : feesOverrideNum;

      const feesCalculated = this.computeFeesFromPhases({
        crewLeadEnabled, crewLeadRate,
        flatRateEnabled, flatRateRate,
        packingEnabled, packingRate,
        unpackingEnabled, unpackingRate,
        loadEnabled, loadRate, crewLeadLoadRate,
        unloadEnabled, unloadRate, crewLeadUnloadRate,
      });

      // Requirement: `#6892` - the fee the calculator itself resolved and saved, READ rather than
      // re-derived, exactly as `tip` and `paid` are read above. This is the figure the Job page and
      // the drill-down report show, so taking it verbatim is what guarantees the three agree --
      // reproducing the calculator's arithmetic here is how they came apart in the first place, and
      // an override was only the case where the reproduction was visibly wrong.
      //
      // `computeFees` takes over from here only once the user edits a checkbox, to preview that
      // edit before Save; the save then returns the authoritative figures again (applyCrewPay).
      //
      // The forwarding flag is re-applied on top because it is read from the INPUT
      // (`crewLaborPaidToFp`) while this amount comes from the last calculator run: a flag set
      // without a recompute would otherwise show a fee on a row the UI has already locked to zero.
      const fees = laborPaidElsewhere ? 0 : money(CREW_PAY_FEES_HEADER);
      const total = this.computeTotal({ fees, bonusEnabled, bonusRate, tip, laborPaidElsewhere });

      result.push({
        jobId,
        jobCode: job.label,
        jobUrl,
        sharedFranchises,
        sharedFranchiseChips,
        customerName,
        lastEventDate,
        creationDate,
        crewMemberId,
        crewMemberName: crew.label,
        crewLeadRate, flatRateRate, packingRate, unpackingRate,
        loadRate, unloadRate, crewLeadLoadRate, crewLeadUnloadRate, bonusRate,
        fees, feesOverride, feesCalculated, tip, paid,
        crewLeadEnabled, flatRateEnabled, packingEnabled, unpackingEnabled,
        loadEnabled, unloadEnabled, bonusEnabled,
        flatRateDisabled: false, packingDisabled: false,
        unpackingDisabled: false, loadDisabled: false, unloadDisabled: false, bonusDisabled: false,
        warnings: [],
        total, unpaid: total - paid,
        payToFp,
        laborPaidElsewhere,
        jobExecutionType,
        jobExecutionLabel: JOB_EXECUTION_LABELS[jobExecutionType] ?? jobExecutionType,
        isThirdParty: jobExecutionType === JOB_EXECUTION_THIRD_PARTY,
        contractForThisJob, achInfo, w9,
        noCrew: false,
        // Overwritten by groupRows once the job's position within its payment period is known.
        jobStripe: false,
      });
    }

    return result;
  }

  // Read-only row for a job with no crew assigned: all pay columns render as "—",
  // nothing is editable, and it never enters pendingByJob (empty crewMemberId).
  // Takes an object rather than a positional list: every field is a string, so a positional
  // signature this long silently accepts two of them swapped. `#6751` would have added a seventh.
  private buildPlaceholderRow(identity: {
    jobId: string;
    jobCode: string;
    jobUrl: string;
    sharedFranchises: string;
    sharedFranchiseChips: FranchiseChip[];
    customerName: string;
    lastEventDate: string;
    creationDate: string;
  }): OtrLaborRow {
    return {
      ...identity,
      crewMemberId: '', crewMemberName: 'No crew assigned',
      crewLeadRate: 0, flatRateRate: 0, packingRate: 0, unpackingRate: 0,
      loadRate: 0, unloadRate: 0, crewLeadLoadRate: 0, crewLeadUnloadRate: 0, bonusRate: 0,
      fees: 0, feesOverride: null, feesCalculated: 0, tip: 0, paid: 0,
      crewLeadEnabled: false, flatRateEnabled: false, packingEnabled: false,
      unpackingEnabled: false, loadEnabled: false, unloadEnabled: false, bonusEnabled: false,
      flatRateDisabled: true, packingDisabled: true, unpackingDisabled: true,
      loadDisabled: true, unloadDisabled: true, bonusDisabled: true,
      warnings: [], total: 0, unpaid: 0,
      payToFp: false,
      laborPaidElsewhere: false,
      // A crewless placeholder renders every pay cell as "—" regardless, so the execution type
      // carries no meaning here; Partner FP keeps it out of the Third-Party suppression path.
      jobExecutionType: JOB_EXECUTION_PARTNER_FP,
      jobExecutionLabel: JOB_EXECUTION_LABELS[JOB_EXECUTION_PARTNER_FP],
      isThirdParty: false,
      contractForThisJob: false, achInfo: false, w9: false,
      noCrew: true,
      // Overwritten by groupRows once the job's position within its payment period is known.
      jobStripe: false,
    };
  }

  // The jobCrewMember report matches crew per event via OPTIONAL MATCH and dedupes to
  // distinct (job, crewMember). A job with a crewed event AND a crewless event (e.g. an
  // estimate with no attendees) therefore yields its real crew rows PLUS a spurious
  // (job, null) row. Keep a "no crew assigned" placeholder only for jobs with no real
  // crew at all, and at most one per job.
  private dropRedundantPlaceholders(rows: OtrLaborRow[]): OtrLaborRow[] {
    const jobsWithCrew = new Set(rows.filter((r) => !r.noCrew).map((r) => r.jobId));
    const seenPlaceholder = new Set<string>();
    return rows.filter((r) => {
      if (!r.noCrew) { return true; }
      if (jobsWithCrew.has(r.jobId)) { return false; }
      if (seenPlaceholder.has(r.jobId)) { return false; }
      seenPlaceholder.add(r.jobId);
      return true;
    });
  }

  private groupRows(laborRows: OtrLaborRow[]): PaymentPeriodGroup[] {
    const periodMap = new Map<string, OtrLaborRow[]>();

    for (const laborRow of laborRows) {
      // Every windowed row has a last-event date, so all rows bucket into a Wed–Tue
      // payment period keyed off that date.
      const periodKey = this.paymentPeriodStart(laborRow.lastEventDate);
      if (!periodMap.has(periodKey)) { periodMap.set(periodKey, []); }
      periodMap.get(periodKey)!.push(laborRow);
    }

    return Array.from(periodMap.entries())
      // Most recent payment period first.
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([periodStart, periodRows]) => {
        // Requirement: `#6751` - jobId is a TIE-BREAKER, not decoration. Customer name alone left a
        // customer with two jobs in one payment period free to interleave their rows (the report is
        // fetched with `sort: null`, so equal names keep whatever order Neo4j returned), which the
        // per-job striping below would then render as split or alternating bands — worse than no
        // striping, because it would draw a job boundary in the wrong place.
        // Requirement: `#6990` - then the job's crew by phase and name (see otr-labor-row-order).
        // Only on (re)load: this is the one caller, so a toggled checkbox never moves a row out
        // from under the cursor; the new order shows on the next refresh.
        const rows = [...periodRows].sort((a, b) =>
          a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' })
          || a.jobId.localeCompare(b.jobId)
          || compareCrewWithinJob(a, b));

        // Zebra-stripe by job, restarting at each payment period so a group reads the same way
        // whichever others are collapsed. Rows are contiguous per job after the sort above, so a
        // flip on each jobId change is all that is needed.
        let previousJobId = '';
        let stripe = false;
        for (const row of rows) {
          if (row.jobId !== previousJobId) {
            stripe = previousJobId === '' ? false : !stripe;
            previousJobId = row.jobId;
          }
          row.jobStripe = stripe;
        }

        const jobCount = new Set(rows.map((r) => r.jobId)).size;
        const start = dayjs(periodStart);
        const end = start.add(6, 'day');
        // Payout is the first Friday after the period end (Wed–Tue period → Friday).
        const FRIDAY = 5;
        const daysUntilPayout = ((FRIDAY - end.day() + 7) % 7) || 7;
        const payout = end.add(daysUntilPayout, 'day');
        return {
          periodStart,
          label: `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`,
          payoutDate: payout.format('MMM D, YYYY'),
          payoutIso: payout.format('YYYY-MM-DD'),
          jobCount,
          rows,
        };
      });
  }

  // Per-job: set each column's disabled state (expected crew size 0 → calculator shows "—")
  // and each row's warnings (over/under crew count, double-assignment, overpay). Mirrors the
  // calculator's crew-pay-table. Used on (re)load and after a full save.
  private applyComputedState(groups: PaymentPeriodGroup[]): void {
    for (const group of groups) {
      const byJob = new Map<string, OtrLaborRow[]>();
      for (const r of group.rows) {
        if (!byJob.has(r.jobId)) { byJob.set(r.jobId, []); }
        byJob.get(r.jobId)!.push(r);
      }
      for (const [ jobId, jobRows ] of byJob) {
        this.applyComputedStateForJob(jobId, jobRows);
      }
    }
  }

  // Recompute only the edited row's job. All of a job's crew rows share one payment-period
  // group, and warnings/disabled state are per-job (no cross-job dependency), so this is a
  // complete recompute for the edit — without walking the whole periodGroups tree on every
  // keystroke.
  private recomputeRowJob(row: OtrLaborRow): void {
    for (const group of this.periodGroups) {
      const jobRows = group.rows.filter((r) => r.jobId === row.jobId);
      if (jobRows.length) {
        this.applyComputedStateForJob(row.jobId, jobRows);
        return;
      }
    }
  }

  private applyComputedStateForJob(jobId: string, jobRows: OtrLaborRow[]): void {
    const COLS = [
      { key: 'crewLead', enabled: 'crewLeadEnabled', disabled: 'crewLeadDisabled', label: 'Crew Lead' },
      { key: 'flatRate', enabled: 'flatRateEnabled', disabled: 'flatRateDisabled', label: 'Reg. Crew' },
      { key: 'packing', enabled: 'packingEnabled', disabled: 'packingDisabled', label: 'Packing' },
      { key: 'unpacking', enabled: 'unpackingEnabled', disabled: 'unpackingDisabled', label: 'Unpacking' },
      { key: 'load', enabled: 'loadEnabled', disabled: 'loadDisabled', label: 'Load' },
      { key: 'unload', enabled: 'unloadEnabled', disabled: 'unloadDisabled', label: 'Unload' },
    ] as const;

    const calc = this.jobCalc.get(jobId);
    // Placeholder (no-crew) rows aren't real crew members: exclude them from the crew
    // count, the assignment/warning math, and the per-row disabled updates.
    const realRows = jobRows.filter((r) => !r.noCrew);
    const crewCount = realRows.length;
    // Expected crew per column (mirrors crew-pay.constants getExpectedCount).
    const expected: Record<string, number> = {
      crewLead: (calc?.rates.crewLead ?? 0) > 0 ? 1 : 0,
      flatRate: Math.max(0, crewCount - 1),
      packing: calc?.crewSize.packing ?? 0,
      unpacking: calc?.crewSize.unpacking ?? 0,
      load: calc?.crewSize.load ?? 0,
      unload: calc?.crewSize.unload ?? 0,
    };

    // Requirement: `#6620` - an outside agency ran a Third-Party job, so the calculator's crew
    // sizes describe a crew this job never used. Comparing against them reports every phase as
    // under-assigned, which is noise rather than a discrepancy: the job is not meant to have crew
    // of its own. One warning replaces the set, and it names the real thing to look at.
    const isThirdPartyJob = realRows.some((r) => r.isThirdParty);

    const jobWarnings: string[] = [];
    if (isThirdPartyJob) {
      if (crewCount > 0) {
        jobWarnings.push('Third-party job: an outside agency ran this move, so it funds no FP labor.'
          + ' Crew are still assigned with pay calculated — confirm that is intended.');
      }
    } else {
      for (const c of COLS) {
        // Requirement: `#6538` - a "Pay Labor to FP" member still works the job — only their payout
        // moved, so they count towards the expected crew size. Their real assignments now come from
        // crewLaborAssignedPhases, so `r[c.enabled]` is already correct for them and needs no
        // special case; treating the override itself as "assigned" would count such a member into
        // every column, including phases they were never assigned to.
        const assigned = realRows.filter((r) => r[c.enabled]).length;
        if (assigned > expected[c.key]) {
          jobWarnings.push(`${c.label}: ${assigned} assigned, exceeds the expected crew size (${expected[c.key]}).`);
        } else if (assigned < expected[c.key]) {
          jobWarnings.push(`${c.label}: ${assigned} assigned, below the expected crew size (${expected[c.key]}).`);
        }
      }
    }

    // Crew Lead is never disabled here — its calculator gate (fpLabor_crewLead) is a
    // persisted rate that can lag the live Job-page calc, which caused a spurious "—".
    // Reg. Crew follows the calculator: disabled on single-crew jobs (expected = 0).
    // The rest follow their (reliable) crew size.
    const bonusDisabled = (calc?.crewSize.driving ?? 0) === 0;
    for (const r of realRows) {
      r.flatRateDisabled = expected.flatRate === 0;
      r.packingDisabled = expected.packing === 0;
      r.unpackingDisabled = expected.unpacking === 0;
      r.loadDisabled = expected.load === 0;
      r.unloadDisabled = expected.unload === 0;
      r.bonusDisabled = bonusDisabled;

      const warnings = [ ...jobWarnings ];
      // Suppressed on Third-Party for the same reason as the counts, and to match the calculator's
      // crew-pay table. The overpay warning below is NOT suppressed: a recorded payment exceeding
      // the total is a real unsettled balance whoever ran the move.
      if (!isThirdPartyJob && r.crewLeadEnabled && r.flatRateEnabled) {
        warnings.push('Assigned as both Crew Lead and Reg. Crew.');
      }
      if (Math.round(r.unpaid * 100) < 0) {
        warnings.push('Paid exceeds the total for this crew member.');
      }
      r.warnings = warnings;
    }
  }

  private paymentPeriodStart(dateStr: string): string {
    const date = dayjs(dateStr);
    if (!date.isValid()) { return dateStr; }
    const daysBack = (date.day() - 3 + 7) % 7;
    return date.subtract(daysBack, 'day').format('YYYY-MM-DD');
  }

  private computeTotal(r: Pick<OtrLaborRow,
    'fees' | 'bonusEnabled' | 'bonusRate' | 'tip' | 'laborPaidElsewhere'
  >): number {
    // Mirrors the calculator's crew-pay-table: total = fees + bonus + tip.
    // "Total fees for services" already sums the enabled component columns
    // (crew lead, reg. crew, packing, unpacking, load, unload), so they must
    // NOT be added again here — doing so double-counts the component pay.
    //
    // Requirement: `#6538` - labor is forwarded to the FP, so the bonus drops out with the fees —
    // but the tip does not. Tips come from the job-level pool rather than the labor buckets, so a
    // forwarded member's total is exactly their tip, and it is still owed to them.
    return r.fees
      + (r.bonusEnabled && !r.laborPaidElsewhere ? r.bonusRate : 0)
      + r.tip;
  }

  // "Total fees for services" as the enabled component columns come to on their own: crew lead,
  // reg. crew, packing, unpacking, load, unload. Mirrors the calculator's `earnedFeesColumnSum`.
  // Bonus and tips are added separately in computeTotal, so they are not included here.
  //
  // Requirement: `#6892` - deliberately NOT override-aware and NOT zeroed for a member paid
  // elsewhere: this is the "what the checkboxes say" figure, which the cell shows alongside an
  // override. `computeFees` below applies both of those rules to produce the amount actually owed.
  private computeFeesFromPhases(r: Pick<OtrLaborRow,
    'crewLeadEnabled' | 'crewLeadRate' |
    'flatRateEnabled' | 'flatRateRate' |
    'packingEnabled' | 'packingRate' |
    'unpackingEnabled' | 'unpackingRate' |
    'loadEnabled' | 'loadRate' | 'crewLeadLoadRate' |
    'unloadEnabled' | 'unloadRate' | 'crewLeadUnloadRate'
  >): number {
    return (r.crewLeadEnabled ? r.crewLeadRate : 0)
      + (r.flatRateEnabled ? r.flatRateRate : 0)
      + (r.packingEnabled ? r.packingRate : 0)
      + (r.unpackingEnabled ? r.unpackingRate : 0)
      + (r.loadEnabled ? this.effectiveLoadRate(r) : 0)
      + (r.unloadEnabled ? this.effectiveUnloadRate(r) : 0);
  }

  // Requirement: `#7194` - mirrors the calculator's crew-pay loop: the member ticked as Crew Lead is
  // paid the lead's share of Load and Unload. Public because the rate hints in the template use it.
  effectiveLoadRate(r: Pick<OtrLaborRow, 'crewLeadEnabled' | 'loadRate' | 'crewLeadLoadRate'>): number {
    return r.crewLeadEnabled ? r.crewLeadLoadRate : r.loadRate;
  }

  effectiveUnloadRate(r: Pick<OtrLaborRow, 'crewLeadEnabled' | 'unloadRate' | 'crewLeadUnloadRate'>): number {
    return r.crewLeadEnabled ? r.crewLeadUnloadRate : r.unloadRate;
  }

  // The fee this member is actually owed, for a row the user is EDITING. On load the saved figure
  // is read straight from the report instead (see parseRows) -- this exists to preview an edit
  // before Save, since a toggled checkbox has no saved figure yet.
  //
  // Resolves exactly as the calculator's crew-pay loop and the Job page's crew-pay table do, and
  // must keep doing so: this dashboard's figures authorise payments, and "Pay all" writes the Total
  // it shows straight into `crewPay__<id>__paid`.
  private computeFees(r: Pick<OtrLaborRow,
    'feesCalculated' | 'feesOverride' | 'laborPaidElsewhere'
  >): number {
    // Requirement: `#6538` - a forwarded member's phase assignments stay set (they size the amount
    // sent to the FP), but none of it is paid here, so the fees they are owed are 0. Checked first,
    // ahead of the override, matching both: an override sizes the money sent to the Franchise
    // Partner, but none of it is paid to the member on this job.
    if (r.laborPaidElsewhere) { return 0; }
    // Requirement: `#6892` - a manual override REPLACES the phase sum; it does not add to it
    // (calculator: `feesOverrideRaw != null ? Number(feesOverrideRaw) : earnedFeesColumnSum`; Job
    // page: `feesOverride ?? computedFees`). Re-deriving the sum here instead is what made the
    // dashboard disagree with both, and paid out the difference.
    //
    // `??` semantics, not `||`: an override of 0 is a real instruction to pay no fees for service.
    return r.feesOverride ?? r.feesCalculated;
  }

  // A placeholder row has no crew member, so there is nothing to show or edit in its pay cells.
  payLocked(row: OtrLaborRow): boolean {
    return row.noCrew;
  }

  // Requirement: `#6892` - whether to show the phase-column total beneath the fee. Only on a row
  // whose fee is manually overridden, because only there do the two figures disagree and only
  // there do the checkboxes stop moving the amount.
  //
  // Suppressed for a member paid elsewhere, mirroring the Job page's own reset affordance
  // (`*ngIf="row.feesHasOverride && !row.laborPaidElsewhere"`): their fee is 0 whatever the
  // override says, so showing a calculated figure beside it would claim pay nobody is receiving.
  showFeesOverride(row: OtrLaborRow): boolean {
    return !row.noCrew && row.feesOverride !== null && !row.laborPaidElsewhere;
  }

  // Static, so the template binding stays reference-stable across change detection. The amounts
  // are on screen already; what the reader needs is why they differ and where to change it.
  readonly feesOverrideTooltip =
    'Manually overridden on the job page. The phase checkboxes below no longer change this amount'
    + ' — clear the override on the job to go back to the calculated total.';

  // Requirement: `#6538` - a member whose labor is forwarded to the FP still has known phase
  // assignments (crewLaborAssignedPhases), so show the real checkboxes instead of "—" — but
  // read-only, and with no rate beside them: every amount is 0, so a rate would read as pay this
  // member is not receiving. Mirrors the calculator's crew-pay table, which disables the same
  // checkboxes and hides the same values while the override is on.
  payReadOnly(row: OtrLaborRow): boolean {
    return row.laborPaidElsewhere;
  }

  // Requirement: `#6620` - "Pay Labor to FP" only means something on a job that HAS a Franchise
  // Partner to forward to. The calculator neutralises the flag outright on the other job types
  // (`payToFp = !zeroesFpPayout && …`), so leaving the checkbox live here lets an operator zero and
  // lock the row while the recompute pays the member in full — and persists a `payToFp__enabled`
  // flag that reappears, silently, if the job is ever corrected back to Partner FP.
  //
  // Mirrors `payToFpLocked` in the calculator's crew-pay table, which locks the same checkbox on the
  // same job types.
  payToFpLocked(row: OtrLaborRow): boolean {
    return jobExecutionZeroesFpPayout(row.jobExecutionType);
  }

  payToFpTooltip(row: OtrLaborRow): string {
    return this.payToFpLocked(row)
      ? `Not available on a ${row.jobExecutionLabel} job: there is no Franchise Partner to forward this crew member's pay to.`
      : `Zero this crew member's pay and forward the amount to the Franchise Partner`;
  }

  private recalculate(row: OtrLaborRow): void {
    // Re-derive fees from the (toggled) component columns so checkbox changes
    // update the Total fees for services, Total and Unpaid live.
    // Requirement: `#6892` - the phase sum is refreshed either way, even while an override
    // supersedes it: the cell shows both, so the "calculated" figure has to follow the checkboxes
    // the user is ticking. Only `fees` stops moving, which is what the calculator does.
    row.feesCalculated = this.computeFeesFromPhases(row);
    row.fees = this.computeFees(row);
    row.total = this.computeTotal(row);
    row.unpaid = row.total - row.paid;
  }

  toggleComponent(
    row: OtrLaborRow,
    metaKey: string,
    field: 'crewLeadEnabled' | 'flatRateEnabled' | 'packingEnabled' | 'unpackingEnabled' | 'loadEnabled' | 'unloadEnabled' | 'bonusEnabled',
    checked: boolean,
  ): void {
    row[field] = checked;
    this.recalculate(row);
    this.recomputeRowJob(row);
    if (!row.crewMemberId) { return; }
    this.queueSave(row.jobId, {
      [`${CALC_INPUT_PREFIX}${row.crewMemberId}__${metaKey}__enabled`]: String(checked),
    });
  }

  // Requirement: `#6538` - "Pay Labor to FP" (per job × crew member): the Franchise Partner pays
  // this member locally, so the calculator zeroes their crew pay and forwards it to the FP. Staged
  // like the pay checkboxes and applied by the batched Save's recompute.
  //
  // Optimistically zero the row so the UI matches what the recompute will return. The phase
  // `*Enabled` flags are left alone: their `__enabled` inputs are untouched in the calculator, and
  // clearing them here would send a wrong value to the server on the next component toggle.
  togglePayToFp(row: OtrLaborRow, checked: boolean): void {
    if (!row.crewMemberId) { return; }
    row.payToFp = checked;
    // Keep the derived flag in step, or this row's pay would not recompute. Safe to OR with the
    // job type: `payToFpLocked` disables the checkbox on the job types that zero the payout, so
    // this only ever runs on Partner FP, where the calculator honours the flag.
    row.laborPaidElsewhere = checked || row.isThirdParty;
    // computeFees/computeTotal both read `laborPaidElsewhere`, so one recalculate covers both
    // directions of this flag. (`#6892` gave the fee a SECOND override, the manual one — it is
    // resolved in computeFees beneath this flag, so a forwarded member still zeroes either way.)
    // Note `paid` is deliberately untouched: a forwarded member is still owed their tip, so any
    // payment already recorded against it stands.
    this.recalculate(row);
    this.recomputeRowJob(row);
    this.queueSave(row.jobId, {
      [`${CALC_INPUT_PREFIX}${row.crewMemberId}__payToFp__enabled`]: String(checked),
      [`${CALC_INPUT_PREFIX}${row.crewMemberId}__payToFp__updatedAt`]: String(Math.floor(Date.now() / 1000)),
    });
  }

  // Contract For This Job (per job × crew member): staged like the pay checkboxes and persisted
  // on the batched Save (updateJob + runJobCalculator). It doesn't affect pay math, so there's
  // no fees/total recompute — just stage the flag + its updatedAt.
  toggleContract(row: OtrLaborRow, checked: boolean): void {
    if (!row.crewMemberId) { return; }
    row.contractForThisJob = checked;
    this.queueSave(row.jobId, {
      [`${CALC_INPUT_PREFIX}${row.crewMemberId}__${CONTRACT_FLAG}__enabled`]: String(checked),
      [`${CALC_INPUT_PREFIX}${row.crewMemberId}__${CONTRACT_FLAG}__updatedAt`]: String(nowSeconds()),
    });
  }

  // ACH Info / W-9 (per crew member, global): persisted immediately via setCrewDocFlags,
  // independent of the Save/Discard banner (which only batches job edits). The flag belongs to
  // the person, so update every row for this crew member across all periods optimistically, and
  // revert them all if the mutation fails.
  //
  // Requirement: `#7001` - the write is authorized through this row's job, so it works for crew
  // whose own zone is outside the current one (the old editProfile path silently updated nobody
  // for them and still toasted "Saved"). Only a confirmed write is snapshotted as saved.
  toggleUserDocFlag(row: OtrLaborRow, flag: UserDocFlag, checked: boolean): void {
    const { crewMemberId } = row;
    if (!crewMemberId) { return; }
    const field: 'achInfo' | 'w9' = flag === 'ach' ? 'achInfo' : 'w9';
    const affected = this.rowsForCrewMember(crewMemberId);
    // The checkbox is two-way bound, so the clicked row already holds the new value by the time
    // onChange runs; its previous value is the opposite of `checked`. Reading it back would make a
    // failed write "revert" the clicked row to the value that just failed to save.
    const previous = affected.map((r) => (r === row ? !checked : r[field]));
    affected.forEach((r) => { r[field] = checked; });

    // Show an inline saving indicator on every affected row's cell while the write is in
    // flight, and toast the outcome — these save immediately, so this is their only feedback.
    const savingKey = this.docFlagSaveKey(crewMemberId, flag);
    this.savingDocFlags.add(savingKey);

    this.subs.push(
      this.setCrewDocFlagsGQL.mutate({
        jobId: row.jobId,
        userId: crewMemberId,
        // Only the toggled flag is sent; the other stays as stored.
        achInfo: flag === 'ach' ? checked : undefined,
        w9: flag === 'w9' ? checked : undefined,
      }).pipe(
        tap(({ data }) => assertCrewDocFlagsSaved(data?.setCrewDocFlags)),
        tap(() => {
          this.snapshotUserDocFlag(affected, field, checked);
          this.notify.success('Saved', `${USER_DOC_FLAG_LABELS[flag]} updated for ${row.crewMemberName}`);
        }),
        catchError((error: ApolloError) => {
          affected.forEach((r, i) => { r[field] = previous[i]; });
          this.notify.apolloError(`Failed to update ${USER_DOC_FLAG_LABELS[flag]} for ${row.crewMemberName}`, error);
          return EMPTY;
        }),
        finalize(() => { this.savingDocFlags.delete(savingKey); }),
      ).subscribe(),
    );
  }

  private docFlagSaveKey(crewMemberId: string, flag: UserDocFlag): string {
    return `${crewMemberId}::${flag}`;
  }

  // A per-user flag saves immediately for the whole person, so all of that crew member's rows
  // show the saving indicator together (keyed by crewMemberId + flag).
  isDocFlagSaving(row: OtrLaborRow, flag: UserDocFlag): boolean {
    return !!row.crewMemberId && this.savingDocFlags.has(this.docFlagSaveKey(row.crewMemberId, flag));
  }

  private rowsForCrewMember(crewMemberId: string): OtrLaborRow[] {
    const rows: OtrLaborRow[] = [];
    for (const group of this.periodGroups) {
      for (const row of group.rows) {
        if (row.crewMemberId === crewMemberId) { rows.push(row); }
      }
    }
    return rows;
  }

  private snapshotUserDocFlag(rows: OtrLaborRow[], field: 'achInfo' | 'w9', value: boolean): void {
    for (const row of rows) {
      const snapshot = this.rowSnapshots.get(this.rowKey(row));
      if (snapshot) {
        snapshot[field] = value;
      }
    }
  }

  // Commit a Paid edit when the user finishes (on blur), not per keystroke — mirrors the
  // Job-page crew-pay-table. The two-way [(ngModel)] keeps row.paid current while typing;
  // on commit we recompute just the row's job and stage the save once.
  commitPaid(row: OtrLaborRow): void {
    this.recalculate(row);
    this.recomputeRowJob(row);
    if (!row.crewMemberId) { return; }
    // Normalize a cleared input (p-inputNumber emits null) so we never stage the string
    // "null" — which would become NaN when the calculator reads it back.
    this.queueSave(row.jobId, {
      [`${CALC_INPUT_PREFIX}${row.crewMemberId}__paid`]: String(row.paid ?? 0),
    });
  }

  // Stage the change into the job's pending set (merged per job). Persisted only on
  // an explicit Save (see saveChanges); the row is also updated optimistically by the caller.
  private queueSave(jobId: string, metadata: Record<string, string>): void {
    const pending = this.pendingByJob.get(jobId) ?? {};
    Object.assign(pending, metadata);
    this.pendingByJob.set(jobId, pending);
  }

  get hasUnsavedChanges(): boolean {
    return this.pendingByJob.size > 0;
  }

  // Back out of all staged edits (the "Discard" in the unsaved-changes banner) by restoring
  // the last authoritative snapshot locally — no refetch — so a bulk "Pay all" can be undone
  // before saving.
  discardChanges(): void {
    if (this.saving) { return; }
    this.pendingByJob.clear();
    this.restoreSnapshot();
  }

  private rowKey(row: OtrLaborRow): string {
    return `${row.jobId}::${row.crewMemberId}`;
  }

  private snapshotRows(shouldSnapshot: (row: OtrLaborRow) => boolean = () => true): void {
    for (const group of this.periodGroups) {
      for (const row of group.rows) {
        if (shouldSnapshot(row)) {
          this.rowSnapshots.set(this.rowKey(row), { ...row, warnings: [ ...row.warnings ] });
        }
      }
    }
  }

  private restoreSnapshot(): void {
    for (const group of this.periodGroups) {
      for (const row of group.rows) {
        const snapshot = this.rowSnapshots.get(this.rowKey(row));
        if (snapshot) {
          Object.assign(row, snapshot, { warnings: [ ...snapshot.warnings ] });
        }
      }
    }
    this.applyComputedState(this.periodGroups);
  }

  // Explicit Save: persist staged jobs' inputs via updateJob, then re-run the calculator
  // server-side (recomputes every crew member per job and writes the reader-visible
  // calculatorOutput__*). The mutation returns the authoritative per-member crew pay,
  // which we apply directly to the rows — no refetch, so there's no read-after-write race
  // and the accordion state is preserved. Work is chunked into batches of SAVE_BATCH_SIZE
  // processed sequentially, so each request stays well under the gateway timeout; for a
  // single batch it's exactly one updateJob + one runJobCalculator.
  saveChanges(): void {
    // Requirement: `#6538` - `stale` blocks the save as well as disabling the button: the staged
    // edits apply to rows that were dropped, so committing them would write figures nobody can
    // see. Discard remains the way out.
    if (this.saving || this.stale || this.pendingByJob.size === 0) { return; }

    const updates = Array.from(this.pendingByJob.entries()).map(([ jobId, metadata ]) => ({ jobId, metadata }));
    const total = updates.length;
    const batches: { jobId: string; metadata: Record<string, string> }[][] = [];
    for (let i = 0; i < updates.length; i += SAVE_BATCH_SIZE) {
      batches.push(updates.slice(i, i + SAVE_BATCH_SIZE));
    }

    let saved = 0;
    this.saving = true;

    this.subs.push(
      from(batches).pipe(
        // updateJob commits before it resolves, so the subsequent recompute reads the
        // freshly-persisted calculatorInput__* for every job in the batch.
        concatMap((batch) =>
          this.updateJobGQL.mutate({ updateJobs: batch }).pipe(
            concatMap(() => this.runJobCalculatorGQL.mutate({ jobIds: batch.map((u) => u.jobId) })),
            tap(({ data }) => {
              for (const jobResult of data?.runJobCalculator?.jobs ?? []) {
                if (!jobResult.success) { continue; } // leave failed jobs staged for retry
                this.pendingByJob.delete(jobResult.jobId);
                saved++;
                for (const cp of this.extractCrewPay(jobResult.state)) {
                  const row = this.findRow(jobResult.jobId, cp.crewMemberId);
                  if (row) { this.applyCrewPay(row, cp); }
                }
              }
            }),
            catchError(() => {
              // This batch stays staged for retry; continue with the remaining batches.
              this.notify.error('Error', 'Failed to save some changes');
              return EMPTY;
            }),
          ),
        ),
        finalize(() => {
          this.saving = false;
          // Refresh disabled/warning state from the applied values.
          this.applyComputedState(this.periodGroups);
          // Update the discard snapshot for successfully-saved jobs (no longer pending), so a
          // later Discard reverts to the saved state; still-pending (failed) jobs keep their
          // pre-edit snapshot so Discard can still revert them.
          this.snapshotRows((row) => !this.pendingByJob.has(row.jobId));
          this.lastReload = Math.floor(Date.now() / 1000);

          if (saved > 0) {
            this.notify.success('Saved', `Recalculated ${saved} of ${total} job${total === 1 ? '' : 's'}`);
          }
          if (saved < total) {
            const failed = total - saved;
            this.notify.error('Error', `${failed} job${failed === 1 ? '' : 's'} failed to save`);
          }
        }),
      ).subscribe(),
    );
  }

  // Pull per-crew-member crew pay out of a runJobCalculator `state` blob. The calculator
  // stores amounts in cents under `crewPay__<crewMemberId>[__<suffix>]`; we convert to
  // dollars. The job-level `crewPay__tips` pool and non-amount keys (e.g. `__enabled`) are
  // ignored. Mirrors the calculator's own key convention (kept here rather than typed into
  // the gateway schema, so the schema stays calculator-agnostic).
  private extractCrewPay(state: Record<string, unknown> | null | undefined): CrewMemberPay[] {
    const byMember = new Map<string, CrewMemberPay>();
    for (const [ key, value ] of Object.entries(state ?? {})) {
      if (typeof value !== 'number') { continue; }
      if (!key.startsWith('crewPay__')) { continue; }
      const rest = key.slice('crewPay__'.length);
      const sepIdx = rest.indexOf('__');
      const crewMemberId = sepIdx === -1 ? rest : rest.slice(0, sepIdx);
      const suffix = sepIdx === -1 ? '' : rest.slice(sepIdx + 2);
      if (crewMemberId === 'tips') { continue; }
      const field = CREW_PAY_OUTPUT_FIELDS[suffix];
      if (!field) { continue; }
      const member = byMember.get(crewMemberId) ?? { crewMemberId };
      member[field] = value / 100;
      byMember.set(crewMemberId, member);
    }
    return Array.from(byMember.values());
  }

  private findRow(jobId: string, crewMemberId: string): OtrLaborRow | undefined {
    for (const group of this.periodGroups) {
      const row = group.rows.find((r) => r.jobId === jobId && r.crewMemberId === crewMemberId);
      if (row) { return row; }
    }
    return undefined;
  }

  // Apply the calculator's authoritative recomputed crew pay (dollars) to a row.
  //
  // Requirement: `#6538` - the phase flags are deliberately NOT re-derived from the component
  // amounts here. Only the user changes a `__enabled` input, so the flags already on the row are
  // current; whereas the amounts are 0 for every phase of a member whose labor is forwarded to the
  // FP, and 0 for any phase whose per-man rate is 0 — so reading them back would blank correct
  // checkboxes on exactly the rows this column was added to fix.
  private applyCrewPay(row: OtrLaborRow, cp: {
    fees?: number | null; tip?: number | null; total?: number | null; paid?: number | null; unpaid?: number | null;
  }): void {
    row.fees = cp.fees ?? 0;
    row.tip = cp.tip ?? 0;
    row.paid = cp.paid ?? 0;
    row.total = cp.total ?? 0;
    row.unpaid = cp.unpaid ?? 0;
  }

  payAllInGroup(group: PaymentPeriodGroup): void {
    for (const row of group.rows) {
      // Requirement: `#6538` - forwarded members are included — their labor went to the FP, but
      // their tip did not, so "Pay all" still has their tip balance to settle.
      if (row.noCrew) { continue; }
      // Only settle rows that still owe something, matching the per-row Pay button's own gate. A
      // row already paid at or above its total — e.g. paid before its labor was forwarded to the
      // FP, which leaves it deliberately overpaid — keeps the amount actually recorded against it;
      // assigning the smaller total here would erase that payment and persist the loss.
      if (!this.hasUnpaidBalance(row)) { continue; }
      row.paid = row.total;
      this.recalculate(row);
      if (row.crewMemberId) {
        this.queueSave(row.jobId, {
          [`${CALC_INPUT_PREFIX}${row.crewMemberId}__paid`]: String(row.paid),
        });
      }
    }
    this.applyComputedState(this.periodGroups);
  }

  togglePeriod(key: string): void {
    if (this.expandedPeriods.has(key)) {
      this.expandedPeriods.delete(key);
    } else {
      this.expandedPeriods.add(key);
    }
  }

  isPeriodExpanded(key: string): boolean {
    return this.expandedPeriods.has(key);
  }

  isPayoutPast(group: PaymentPeriodGroup): boolean {
    // Unpaid balances only matter once the group's payout date has passed (future
    // payment periods aren't overdue yet).
    return !!group.payoutIso && dayjs(group.payoutIso).isBefore(dayjs(), 'day');
  }

  hasUnpaidBalance(row: OtrLaborRow): boolean {
    // Compare on whole cents so a balance that rounds to $0.00 in the display
    // doesn't keep the row flagged unpaid due to floating-point residue.
    return Math.round(row.unpaid * 100) > 0;
  }

  showUnpaidWarning(group: PaymentPeriodGroup, row: OtrLaborRow): boolean {
    // Only highlight an unpaid row once its payout date is overdue.
    return this.isPayoutPast(group) && this.hasUnpaidBalance(row);
  }

  unpaidJobCount(group: PaymentPeriodGroup): number {
    // Distinct jobs with at least one crew member still owed money, but only
    // once payout is overdue. Computed live so the header tracks Paid edits.
    if (!this.isPayoutPast(group)) { return 0; }
    return new Set(
      group.rows.filter((r) => this.hasUnpaidBalance(r)).map((r) => r.jobId),
    ).size;
  }

  noCrewJobCount(group: PaymentPeriodGroup): number {
    // Distinct jobs in this period with no crew assigned (shown as placeholder rows).
    return new Set(
      group.rows.filter((r) => r.noCrew).map((r) => r.jobId),
    ).size;
  }

  groupHasUnpaid(group: PaymentPeriodGroup): boolean {
    // Whether any crewed row in the period still has an outstanding balance — gates the
    // "Pay all" button so it disables once everyone is paid.
    return group.rows.some((r) => !r.noCrew && this.hasUnpaidBalance(r));
  }

  markPaidInFull(row: OtrLaborRow): void {
    row.paid = row.total;
    this.commitPaid(row);
  }

  get isEmpty(): boolean {
    // Requirement: `#6538` - `stale` excluded deliberately: the empty state claims there are no
    // jobs in the period, which would be a false statement when the rows were dropped because the
    // response was unusable. The error message says what actually happened instead.
    //
    // `error` is excluded for the same reason, and it is not covered by `stale`. A request that
    // fails outright never sets `stale` — it lands in `catchError` — so on a first load, or on a
    // refresh after a stale response, this read "no jobs in this period" when the truth is that we
    // never found out. Safe to consult here because `error` is load-only: the three writes are all
    // in the load flow, and Save reports through `notify` instead.
    return !this.loading && !this.stale && !this.error && this.periodGroups.length === 0;
  }

  // Requirement: #6598 - exporting is blocked while there are staged edits. This is a payroll
  // file: it must never carry values that were never committed to the system. Save or Discard
  // re-enables it (both clear pendingByJob).
  //
  // savingDocFlags is included even though ACH/W-9 bypass pendingByJob: toggleUserDocFlag
  // updates the row optimistically and rolls it back if setCrewDocFlags fails, so exporting mid
  // write can capture a value that never lands. Cleared by the mutation's finalize().
  //
  // An empty view is blocked whatever the reason. `stale` already covers a rejected response, but
  // not a request that failed outright (that path never sets it) — and in both cases the file would
  // contain headers only, which reads in a spreadsheet as "nobody to pay this period". Checking the
  // rows rather than each way of arriving at zero of them also covers a genuinely empty period,
  // where a headers-only file is equally pointless.
  get exportDisabled(): boolean {
    return this.loading || this.saving || this.stale || this.hasUnsavedChanges
      || this.hasPendingDocFlagWrites || this.periodGroups.length === 0;
  }

  get exportDisabledReason(): string {
    // Requirement: `#6538` - a stale view has no rows, so the file would contain headers only —
    // indistinguishable from a payment period with nobody to pay, which is the wrong conclusion
    // to hand someone in a spreadsheet.
    if (this.stale) {
      return 'Refresh the dashboard before exporting — the current figures could not be loaded';
    }
    if (this.hasUnsavedChanges || this.saving || this.hasPendingDocFlagWrites) {
      return 'Save or discard your changes before exporting';
    }
    // Distinguish the two ways of having nothing to export, because the advice differs: a failed
    // load is worth retrying, an empty period is not. Without this the tooltip on a disabled button
    // would still invite the user to copy or download.
    if (this.periodGroups.length === 0) {
      return this.error
        ? 'Refresh the dashboard before exporting — the current figures could not be loaded'
        : 'There is nothing to export for this period';
    }
    return 'Copy every payment period below, or use the arrow to download as CSV';
  }

  private get hasPendingDocFlagWrites(): boolean {
    return this.savingDocFlags.size > 0;
  }

  // Whole-view export: every loaded payment period in one file. The Payment Period and Payout
  // Date columns are what keep rows attributable once the accordion nesting is flattened away.
  private downloadCsv(): void {
    this.download(
      buildLaborExportRows(this.periodGroups, { includePeriodColumns: true }),
      this.displayPeriod,
    );
  }

  // Requirement: `#6751` - single-payroll-period copy: one Wed–Tue group, as handed to whoever cuts
  // the cheques. This replaced a CSV download of the same rows — the destination was always a
  // spreadsheet, and the download made that a two-step trip through a file nobody kept.
  //
  // WITH the period columns, unlike the download it replaces. That file carried the period in its
  // filename; a clipboard paste has no filename, so without these two columns the pasted block
  // would not say which payroll period it authorises.
  copyGroupToClipboard(group: PaymentPeriodGroup): void {
    this.copyRows(buildLaborExportRows([ group ], { includePeriodColumns: true }));
  }

  copyToClipboard(): void {
    this.copyRows(buildLaborExportRows(this.periodGroups, { includePeriodColumns: true }));
  }

  // Requirement: `#6765` - spreadsheet formula-injection guard. `Customer` and `Crew Member` are
  // free text a user can enter, and both destinations here are a spreadsheet, so a customer named
  // `=HYPERLINK("http://evil","click")` would paste in as a live formula. papaparse prefixes any
  // matching value with a quote so it lands as text.
  //
  // `-` is handled by a NEGATIVE LOOKAHEAD rather than by papaparse's default set
  // (`/^[=+\-@\t\r].*$/`) or by dropping it entirely. Both alternatives are wrong here:
  //
  //   - Keeping the default escapes negative money. This export writes amounts as a bare 2dp
  //     number precisely so Sheets parses them as numbers (`money()` in otr-labor-export.ts), and a
  //     negative `Unpaid` — an overpayment, a real state — renders `-123.45`. The default rewrites
  //     that to `'-123.45`, turning the column most worth summing into text.
  //   - Dropping `-` outright lets a formula through: `-1+HYPERLINK("http://evil")` is a live
  //     formula that starts with a minus.
  //
  // So: escape a leading `-` UNLESS the entire value is this export's numeric format. Verified
  // against the pinned papaparse 5.3.2, which accepts a RegExp and applies it with `.test()`.
  //
  // COUPLING TO WATCH: the lookahead encodes `toFixed(2)` — digits, one dot, exactly two decimals.
  // If `money()` ever emits thousands separators or a currency symbol, negatives stop matching it
  // and start being escaped again, silently turning them back into text. Change both together.
  private static readonly SPREADSHEET_FORMULA_PATTERN = /^(?:[=+@\t\r]|-(?!\d+\.\d{2}$))/;

  // Tab-delimited, not comma: the destination is a spreadsheet, and a tab-separated paste lands in
  // columns directly whereas CSV text goes into one cell until the user runs Text to Columns.
  private copyRows(rows: string[][]): void {
    this.clipboard.copy(unparse(rows, {
      delimiter: '\t',
      escapeFormulae: DashboardOtrLaborTrackingComponent.SPREADSHEET_FORMULA_PATTERN,
    }));
    this.notify.success('Copied to clipboard');
  }

  private download(rows: string[][], periodLabel: string): void {
    const csv = unparse(rows, {
      escapeFormulae: DashboardOtrLaborTrackingComponent.SPREADSHEET_FORMULA_PATTERN,
    });
    const blob = new Blob([ csv ], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = laborExportFileName({
      zoneName: this.brandingService.currentZone().value?.name || 'current zone',
      periodLabel,
      timestamp: dayjs().format('YYYY/MM/DD HH:mm'),
    });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

}
